/**
 * Module Stats Store - Store global para estadísticas de módulos
 *
 * @version 1.0
 * @updated 2026-02-25
 * @module core/module-store
 *
 * @description
 * Store Zustand que mantiene estadísticas actualizadas de cada módulo.
 * Los paneles reportan sus datos cuando cargan elementos.
 */

import { create } from 'zustand';
import type { ModuleId } from './types';

/**
 * Estadísticas de un tipo de elemento
 */
export interface ElementTypeStats {
  /** Nombre del tipo (ej: "Árboles", "Paradas") */
  typeName: string;
  /** Cantidad de elementos */
  count: number;
}

/**
 * Estadísticas de un módulo
 */
export interface ModuleStatsState {
  /** Total de elementos en el módulo */
  totalElements: number;
  /** Desglose por tipo de elemento */
  elementTypes: Record<string, ElementTypeStats>;
  /** Última actualización (timestamp) */
  lastUpdated: number;
}

/**
 * Estado del store
 */
interface StatsState {
  /** Estadísticas por módulo */
  modules: Record<ModuleId, ModuleStatsState>;
}

/**
 * Acciones del store
 */
interface StatsActions {
  /**
   * Actualiza las estadísticas de un tipo de elemento específico
   */
  updateElementType: (
    moduleId: ModuleId,
    typeKey: string,
    typeName: string,
    count: number
  ) => void;

  /**
   * Limpia las estadísticas de un módulo
   */
  clearModule: (moduleId: ModuleId) => void;

  /**
   * Obtiene las estadísticas de un módulo
   */
  getModuleStats: (moduleId: ModuleId) => ModuleStatsState | null;
}

/**
 * Store completo
 */
type ModuleStatsStore = StatsState & StatsActions;

/**
 * Estado inicial vacío
 */
const EMPTY_MODULE_STATS: ModuleStatsState = {
  totalElements: 0,
  elementTypes: {},
  lastUpdated: Date.now(),
};

/**
 * Hook del store
 */
export const useModuleStatsStore = create<ModuleStatsStore>((set, get) => ({
  // Estado inicial (parcialmente poblado en runtime)
  modules: {} as Record<ModuleId, ModuleStatsState>,

  // Acciones
  updateElementType: (moduleId, typeKey, typeName, count) => {
    set((state) => {
      const moduleStats = state.modules[moduleId] || { ...EMPTY_MODULE_STATS };

      // Actualizar tipo específico
      const updatedTypes = {
        ...moduleStats.elementTypes,
        [typeKey]: { typeName, count },
      };

      // Calcular total
      const totalElements = Object.values(updatedTypes).reduce(
        (sum, type) => sum + type.count,
        0
      );

      return {
        modules: {
          ...state.modules,
          [moduleId]: {
            totalElements,
            elementTypes: updatedTypes,
            lastUpdated: Date.now(),
          },
        },
      };
    });
  },

  clearModule: (moduleId) => {
    set((state) => {
      const { [moduleId]: _, ...rest } = state.modules;
      return { modules: rest as Record<ModuleId, ModuleStatsState> };
    });
  },

  getModuleStats: (moduleId) => {
    return get().modules[moduleId] || null;
  },
}));
